"use client";

import Link from "next/link";
import {
  CalendarDays,
  Flame,
  History,
  Sparkles,
  Trophy,
  type LucideIcon,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

interface GuestStudyStartDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** "회원가입없이 보기" — 이 모달을 닫고 이어학습 모달(ContinueStudyDialog)을 연다. */
  onBrowseAsGuest: () => void;
}

/** 로그인만 하면(=무료 Free 등급) 쓸 수 있는 기능 카드. 정보 표시용(클릭 불가).
 *  ⚠️ 오답노트·진도추적(공부 진행상황)·정답률 통계·필기는 Premium/Starter 유료 기능이라
 *  여기 넣지 않는다(PASS_FEATURES 참고). 아래 4종은 페이월 게이팅이 없는 무료 로그인 기능.
 *  파스텔 아이콘 칩은 ContinueStudyDialog 의 CATEGORY_META 와 동일한 light/dark 페어
 *  규칙(dark-mode-colors.md §2). */
const FREE_FEATURES: ReadonlyArray<{
  icon: LucideIcon;
  iconClass: string;
  title: string;
  desc: string;
}> = [
  {
    icon: History,
    iconClass: "bg-sky-100 text-sky-600 dark:bg-sky-950/60 dark:text-sky-300",
    title: "이어서 공부하기",
    desc: "마지막에 보던 페이지부터 바로",
  },
  {
    icon: Trophy,
    iconClass:
      "bg-amber-100 text-amber-600 dark:bg-amber-950/60 dark:text-amber-300",
    title: "도전과제 배지",
    desc: "공부하면서 배지를 모아요",
  },
  {
    icon: Flame,
    iconClass:
      "bg-rose-100 text-rose-600 dark:bg-rose-950/60 dark:text-rose-300",
    title: "출석 연속 학습일",
    desc: "매일 공부하는 습관 기록",
  },
  {
    icon: CalendarDays,
    iconClass:
      "bg-emerald-100 text-emerald-600 dark:bg-emerald-950/60 dark:text-emerald-300",
    title: "시험 D-day",
    desc: "시험까지 남은 날 카운트다운",
  },
];

/**
 * 홈 "공부 시작하기" 비회원 소개 모달 (JCG-2167).
 *
 * 비로그인 사용자가 CTA 를 누르면 열린다. 로그인(=회원가입)만 하면 무료로 쓸 수 있는
 * /my 대시보드 기능들을 카드 그리드로 소개하고, 두 갈래로 안내한다:
 *  - "회원가입하기"(primary): `/sign-up` 페이지로 **이동**(Link). Clerk `openSignUp` 모달이
 *    아니라 페이지 이동이어야 가입 전환 트래커(Naver Premium Log·Meta Pixel·GA)가 동작한다.
 *  - "회원가입없이 보기"(secondary): `onBrowseAsGuest()` — 부모가 이 모달을 닫고
 *    ContinueStudyDialog(이어학습 모달)를 연다. 사이트는 로그인 없이도 열람 가능.
 *
 * 색상: 홈 히어로/사이드바 카드와 동일한 amber/orange 파스텔 톤 + 다크모드 페어
 * (dark-mode-colors.md). 기능 카드 아이콘 칩 스타일은 ContinueStudyDialog 와 일치.
 */
export default function GuestStudyStartDialog({
  open,
  onOpenChange,
  onBrowseAsGuest,
}: GuestStudyStartDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[calc(100vw-1rem)] max-w-lg overscroll-contain p-4 sm:max-w-lg sm:p-6">
        <DialogHeader className="min-w-0 pr-8 text-left">
          <span className="inline-flex w-fit items-center gap-1.5 rounded-full bg-amber-100 px-2.5 py-1 text-xs font-bold text-amber-700 dark:bg-amber-950/60 dark:text-amber-300">
            <Sparkles className="h-3.5 w-3.5" aria-hidden="true" />
            전부 무료
          </span>
          <DialogTitle className="mt-2 text-lg">
            로그인하면 이런 걸 무료로 쓸 수 있어요
          </DialogTitle>
          <DialogDescription>
            결제 없이, 회원가입만 하면 바로 쓸 수 있어요.
          </DialogDescription>
        </DialogHeader>

        <ul className="grid min-w-0 grid-cols-1 gap-2 sm:grid-cols-2">
          {FREE_FEATURES.map((feature) => {
            const Icon = feature.icon;
            return (
              <li
                key={feature.title}
                className="flex min-w-0 items-center gap-3 overflow-hidden rounded-2xl border border-border bg-card p-3"
              >
                <span
                  className={`grid h-10 w-10 shrink-0 place-items-center rounded-xl ${feature.iconClass}`}
                >
                  <Icon className="h-5 w-5" aria-hidden="true" />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-bold text-foreground">
                    {feature.title}
                  </span>
                  <span className="mt-0.5 block truncate text-xs text-muted-foreground">
                    {feature.desc}
                  </span>
                </span>
              </li>
            );
          })}
        </ul>

        <div className="mt-1 flex min-w-0 flex-col gap-2">
          <Link
            href="/sign-up"
            onClick={() => onOpenChange(false)}
            className="inline-flex w-full items-center justify-center gap-1.5 rounded-full bg-gradient-to-r from-amber-500 to-orange-500 px-5 py-2.5 text-sm font-bold text-white shadow-md shadow-orange-500/25 transition-transform hover:-translate-y-0.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 focus-visible:ring-offset-2"
          >
            회원가입하고 무료로 시작하기
          </Link>
          <button
            type="button"
            onClick={onBrowseAsGuest}
            className="inline-flex w-full items-center justify-center rounded-full border border-border bg-transparent px-5 py-2.5 text-sm font-semibold text-muted-foreground transition-colors hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 focus-visible:ring-offset-2"
          >
            회원가입없이 둘러보기
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
